import { FrontSide, LinearFilter, Mesh, RGBAFormat, Scene, ShaderMaterial, Texture, Uniform, Vector2, Vector4, WebGLRenderTarget, } from 'three';
import SphereCameraControls, { CameraEvent } from 'shared/renderingEngine/CameraControls';
import Shaders from 'shared/renderingEngine/shaders/Shaders';
import { SphereTextureGeometry } from 'shared/geometries/SphereTextureGeometry';
import planogramConfig from 'shared/config/PlanogramConfig';
import Cameras2D from './Cameras2D';
import SphereCamera from './SphereCamera';
import { assertDefined, debugFloatPrameter } from '../utils/debug';
const FLAT_SUPERSAMPLE = debugFloatPrameter('FLAT_SUPERSAMPLE', 2);
// TODO: should not be a singleton
export default class SphereRenderingEngine {
    constructor() {
        this.sphereScene = new Scene();
        // TODO: investigate, takes 300ms
        this.sphereGeometry = new SphereTextureGeometry({
            x: 0,
            y: planogramConfig.HEIGHT / 2,
            width: planogramConfig.WIDTH,
            height: planogramConfig.HEIGHT,
        });
        this.setupSphereScene();
    }
    resolution(output) {
        if (this.renderTarget === undefined)
            output.set(0, 0);
        else
            output.set(this.renderTarget.width, this.renderTarget.height).divideScalar(FLAT_SUPERSAMPLE);
        return output;
    }
    updateRenderTaget(canvasSize, capabilities) {
        // TODO: consider rounding to nearest power of 2?
        const maxTextureSize = capabilities.maxTextureSize;
        const largerSide = Math.max(canvasSize.x, canvasSize.y);
        const multipliers = new Vector2(canvasSize.x, canvasSize.y).divideScalar(largerSide);
        const largerSideResolution = largerSide * FLAT_SUPERSAMPLE;
        multipliers.multiplyScalar(maxTextureSize / Math.max(largerSideResolution, maxTextureSize));
        const width = multipliers.x * largerSideResolution;
        const height = Math.round(multipliers.y * largerSideResolution);
        if (this.renderTarget !== undefined) {
            if (this.renderTarget.width === width && this.renderTarget.height === height)
                return;
            else
                this.renderTarget.dispose();
        }
        this.renderTarget = new WebGLRenderTarget(width, height, {
            minFilter: LinearFilter,
            magFilter: LinearFilter,
            format: RGBAFormat,
        });
        this.renderTarget.texture.repeat = new Vector2(0, 0);
        this.textureMaterialUniforms.imageTexture.value = this.renderTarget.texture;
    }
    set canvasSize(canvasSize) {
        this._canvasSize = canvasSize;
        this.cameraControls.sphereCamera.canvasSize = canvasSize;
        this.cameras2D.canvasSize = canvasSize;
        this.cameras2D.updateFromSphereCamera(this.cameraControls.sphereCamera);
    }
    get canvasSize() {
        return this._canvasSize;
    }
    init(scene2D, canvasSize, horizonLine, initialZoom = 1, startingPoint = 0) {
        this.cameras2D = new Cameras2D(initialZoom, startingPoint, canvasSize, this.textureMaterialUniforms);
        const sphereCamera = new SphereCamera(canvasSize, horizonLine, startingPoint);
        this.cameraControls = new SphereCameraControls(sphereCamera);
        this.cameraControlsListener = () => {
            this.cameras2D.updateFromSphereCamera(this.cameraControls.sphereCamera);
        };
        this.cameraControls.addEventListener(CameraEvent.CameraUpdate, this.cameraControlsListener);
        this.scene2D = scene2D;
        this.canvasSize = canvasSize;
    }
    setupSphereScene() {
        const textureMaterialUniforms = {
            imageTexture: new Uniform(new Texture()),
            uvScale: new Uniform(new Vector2(1, 1)),
            uvOffset: new Uniform(new Vector2(0.5, 0.5)),
            backgroundColor: new Uniform(new Vector4(1, 1, 1, 1)),
        };
        const materialScreen = new ShaderMaterial({
            uniforms: textureMaterialUniforms,
            vertexShader: Shaders.renderingTextureVertexShader,
            fragmentShader: Shaders.textureFragmentShader,
            side: FrontSide,
        });
        this.textureMaterialUniforms = textureMaterialUniforms;
        const sphereMesh = new Mesh(this.sphereGeometry.geometry, materialScreen);
        sphereMesh.rotation.y = Math.PI;
        this.sphereScene.add(sphereMesh);
    }
    // TODO: take renderer in constructor
    render(renderer) {
        this.updateRenderTaget(this.canvasSize, renderer.capabilities);
        assertDefined(this.renderTarget, 'Failed to create render target');
        const camera2D = this.cameras2D.mainCamera;
        if (camera2D === undefined)
            return;
        this.cameraControls.prepareToRender();
        renderer.autoClear = true;
        renderer.setRenderTarget(this.renderTarget);
        renderer.render(this.scene2D, camera2D);
        renderer.autoClear = false;
        renderer.setRenderTarget(null);
        renderer.render(this.sphereScene, this.cameraControls.sphereCamera.camera);
    }
    dispose() {
        this.cameraControls.removeEventListener(CameraEvent.CameraUpdate, this.cameraControlsListener);
    }
}
export const sphereRenderingEngine = new SphereRenderingEngine();
